/**
 * Reseller Overview (TMMPDM-0015 / 0022, reorganized TMMPDM-0052). Standing alerts stay
 * above the summary cards. Identity (slug, id, created) and Rename / Suspend / More
 * actions live in the object header. Brand stays in a compact Details block.
 * TMMPDM-0017: tmm-api `PATCH` / `DELETE /v1/admin/resellers/{id}`; a 502
 * `SAAS_AUTH_OUTCOME_UNKNOWN` asks the page to re-read the reseller (`onReload`).
 * TMMPDM-0023: standing comes from the page's standing read, never from `reseller`.
 * TMMPDM-0025: Keep this reseller… on the DEACTIVATION_DUE alert. Create brand when
 * there is no `defaultBrandId`.
 * TMMPDM-0026: Reactivate… (an info Alert) follows `reactivateOffered`, the server verdict,
 * not the standing state.
 * TMMPDM-0030: the out-of-band warning. After Reactivate…, focus goes to the heading.
 * TMMPDM-0037: End probation… on PROBATION. TMMPDM-0041: clear's 409 hands over to
 * Reactivate… when `reactivateOffered`, else names the status toggle (TMMPDM-0069). TMMPDM-0048:
 * the warning is `outOfBand === ACTIVE_IN_SAAS_AUTH`.
 * TMMPDM-0050: no stand-alone Reactivate… for an ordinary suspension.
 * TMMPDM-0088: two labelled regions. Needs you holds the four standing alerts, then the page's
 * other items (`extraNeedsYou`), or "Nothing needs you right now."; At a glance holds the
 * platform-managed note, the cards and the tenant's figures. Details and Create brand moved to
 * Settings › Details and brand (`PartnerDetailsPanel`).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/format';
import { useCopy } from '@/hooks/useCopy';
import { SectionCard, useNotify } from '@/components/page';
import type { ResellerDto, ResellerOutOfBand, ResellerStandingState } from '@/api/tmm/types';
import { ClearStandingDialog, type ClearStandingMode } from './ClearStandingDialog';
import { ReactivateResellerDialog } from './ReactivateResellerDialog';

interface ResellerOverviewPanelProps {
  cards?: ReactNode;
  platformManagedSince?: string | null;
  reseller: ResellerDto;
  /**
   * From the page's `GET …/standing` read (TMMPDM-0023 — `reseller.standing` is list-only);
   * `null` while loading or failed: no chip, and Deactivate stays available.
   */
  standingState: ResellerStandingState | null;
  /** TMMPDM-0046: that standing read is in flight (not failed) — Reactivate… waits for it. */
  standingLoading: boolean;
  /** The standing read failed. The Standing card retries it. */
  standingFailure: unknown;
  onStandingRetry: () => void;
  /** The same standing read's `outOfBand` (TMMAPI-0111); `null` while loading, failed, or in agreement. */
  outOfBand: ResellerOutOfBand | null;
  reactivateOffered: boolean;
  /** Re-read the reseller (after an unconfirmed write). */
  onReload: () => void;
  /** Move focus to the reseller heading (after a Reactivate… write removes the alert's button). */
  onFocusHeading: () => void;
  /** Header More actions — the alert uses the same dialogs. */
  onOpenDeactivate: () => void;
  /** A header write is in flight. */
  actionsBusy: boolean;
  /** The page's other Needs-you items, in order, already filtered (each from one server field). */
  extraNeedsYou?: ReactNode[];
  /** True while a read a Needs-you item rides on is still in flight: no empty line yet. */
  needsYouPending?: boolean;
  /** The rest of At a glance, after the cards. */
  glance?: ReactNode;
}

export function ResellerOverviewPanel({
  reseller,
  platformManagedSince,
  standingState,
  standingLoading,
  outOfBand,
  reactivateOffered,
  onReload,
  onFocusHeading,
  onOpenDeactivate,
  actionsBusy,
  cards,
  extraNeedsYou = [],
  needsYouPending = false,
  glance,
}: ResellerOverviewPanelProps) {
  const { t } = useCopy();
  const notify = useNotify();
  const { i18n } = useTranslation();
  const [actionError, setActionError] = useState<string | null>(null);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const [clearMode, setClearMode] = useState<ClearStandingMode | null>(null);
  const [reactivateOpen, setReactivateOpen] = useState(false);
  const [focusHeadingToken, setFocusHeadingToken] = useState(0);
  const busy = actionsBusy;

  useEffect(() => {
    if (focusHeadingToken > 0) onFocusHeading();
  }, [focusHeadingToken, onFocusHeading]);

  const reloadAfterUnknownOutcome = () => {
    setOutcomeUnknown(true);
    onReload();
  };

  const openClear = (mode: ClearStandingMode) => {
    setActionError(null);
    setOutcomeUnknown(false);
    setClearMode(mode);
  };

  const onCleared = (mode: ClearStandingMode) => {
    notify.success(
      mode === 'keep'
        ? t(
            'resellers.overview.keep.done',
            'Kept. The deactivation flag is cleared; its shortfall charges stay open.',
          )
        : t(
            'resellers.standing.endProbation.done',
            'Probation ended. The reseller is in good standing.',
          ),
    );
    onReload();
  };

  const onClearConflict = (mode: ClearStandingMode) => {
    setActionError(
      mode === 'keep'
        ? t(
            'resellers.overview.keep.conflict',
            'This reseller is no longer due for deactivation, so there was nothing to clear. The screen has been reloaded.',
          )
        : t(
            'resellers.standing.endProbation.conflict',
            'This reseller is no longer on probation, so there was nothing to end. The screen has been reloaded.',
          ),
    );
    onReload();
  };

  const openReactivate = () => {
    setActionError(null);
    setOutcomeUnknown(false);
    setReactivateOpen(true);
  };

  const standingAlerts = [
    standingState === 'DEACTIVATION_DUE' ? (
      <Alert
        key="deactivationDue"
        severity="error"
        action={
          <Stack direction="row" spacing={1}>
            <Button color="inherit" size="small" onClick={() => openClear('keep')} disabled={busy}>
              {t('resellers.overview.keep.button', 'Keep this reseller…')}
            </Button>
            <Button color="inherit" size="small" onClick={onOpenDeactivate}>
              {t('resellers.overview.deactivate', 'Deactivate…')}
            </Button>
          </Stack>
        }
      >
        {t(
          'resellers.overview.deactivationDue',
          'This partner missed its minimum through probation. Keep it and review next month, or deactivate it so the platform manages it in place.',
        )}
      </Alert>
    ) : null,
    standingState === 'PROBATION' ? (
      <Alert
        key="probation"
        severity="warning"
        action={
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              size="small"
              onClick={() => openClear('endProbation')}
              disabled={busy}
            >
              {t('resellers.standing.endProbation.button', 'End probation…')}
            </Button>
          </Stack>
        }
      >
        {t(
          'resellers.overview.probation',
          'This reseller is on probation for missing its minimum. End probation if it has recovered.',
        )}
      </Alert>
    ) : null,
    outOfBand === 'ACTIVE_IN_SAAS_AUTH' ? (
      <Alert key="outOfBand" severity="warning" role="alert">
        {t(
          'resellers.overview.deactivatedButActive',
          'Active in saas-auth, so its users may be able to sign in. Reactivate it here, or ask the platform operator to suspend it again in saas-admin.',
        )}
      </Alert>
    ) : null,
    reactivateOffered ? (
      <Alert
        key="reactivate"
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={openReactivate} disabled={busy}>
            {t('resellers.reactivate.button', 'Reactivate…')}
          </Button>
        }
      >
        {standingState === 'DEACTIVATED'
          ? t(
              'resellers.overview.deactivated',
              "This partner is deactivated. Its administrators can't sign in; its tenant keeps selling.",
            )
          : t(
              'resellers.overview.takeoverReactivate',
              'This partner can be reactivated during the take-over.',
            )}
      </Alert>
    ) : null,
  ].filter((item) => item !== null);
  const needsYou = [...standingAlerts, ...extraNeedsYou];

  return (
    <Box>
      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}
      {outcomeUnknown ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setOutcomeUnknown(false)}>
          {t(
            'common.outcomeUnknown',
            "We couldn't confirm the change. The screen has been reloaded — check before trying again.",
          )}
        </Alert>
      ) : null}

      <Box sx={{ mb: 4 }}>
        <SectionCard
          id="partner-needs-you"
          title={t('partners.needsYou.title', 'Needs you')}
          subtitle={t(
            'partners.needsYou.caption',
            "Things blocking this partner now. Each one opens where it's fixed.",
          )}
        >
          {needsYou.length > 0 ? (
            <Stack spacing={2}>{needsYou}</Stack>
          ) : (
            <Typography variant="body2">
              {needsYouPending
                ? t('partners.needsYou.checking', 'Checking what needs you…')
                : t('partners.needsYou.empty', 'Nothing needs you right now.')}
            </Typography>
          )}
        </SectionCard>
      </Box>

      <Box component="section" aria-labelledby="partner-at-a-glance">
        <Typography id="partner-at-a-glance" variant="h2" component="h2">
          {t('partners.atAGlance.title', 'At a glance')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('partners.atAGlance.caption', 'Read-only. Open a card to see its report.')}
        </Typography>
        {platformManagedSince ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t(
              'partners.platformManaged.alert',
              'Platform-managed since {{date}}. Settlements are retained; reactivate to resume payouts.',
              { date: formatDate(platformManagedSince, i18n.language) },
            )}
          </Alert>
        ) : null}
        {cards}
        {glance}
      </Box>

      <ClearStandingDialog
        open={clearMode !== null}
        mode={clearMode ?? 'keep'}
        reseller={reseller}
        onClose={() => setClearMode(null)}
        onCleared={() => onCleared(clearMode ?? 'keep')}
        onConflict={() => onClearConflict(clearMode ?? 'keep')}
        onOutcomeUnknown={reloadAfterUnknownOutcome}
        onSuspended={onReload}
        standingPending={standingLoading}
        onUseReactivate={openReactivate}
        reactivateOffered={reactivateOffered}
      />
      <ReactivateResellerDialog
        open={reactivateOpen}
        reseller={reseller}
        standingState={standingState}
        onClose={() => setReactivateOpen(false)}
        onReactivated={() => {
          notify.success(
            t('resellers.reactivate.done', '{{name}} is active again.', { name: reseller.name }),
          );
          onReload();
          setFocusHeadingToken((n) => n + 1);
        }}
        onConflict={() => {
          setActionError(
            t(
              'resellers.reactivate.conflict',
              "Reactivate was refused because this reseller's status changed. The screen has been reloaded.",
            ),
          );
          onReload();
          setFocusHeadingToken((n) => n + 1);
        }}
        onNotDeactivated={() => {
          setActionError(
            t(
              'resellers.reactivate.notDeactivated',
              "This partner isn't deactivated. Use Reactivate on the status toggle to lift an ordinary suspension.",
            ),
          );
          onReload();
        }}
        onOutcomeUnknown={() => {
          reloadAfterUnknownOutcome();
          setFocusHeadingToken((n) => n + 1);
        }}
      />
    </Box>
  );
}
